import React, { useState, useEffect } from 'react';
import { feedAPI, companyAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Plus, Loader2, Newspaper } from 'lucide-react';
import { toast } from 'sonner';
import CreatePostModal from '../components/feeds/CreatePostModal';
import FeedPostCard from '../components/feeds/FeedPostCard';
import ModuleDisabled from '../components/shared/ModuleDisabled';

const Feeds = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [error, setError] = useState(null);
    const [editingPost, setEditingPost] = useState(null);
    const [company, setCompany] = useState(null);
    const { isAdminOrHR } = useAuth();

    const fetchPosts = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const response = await feedAPI.getFeeds();
            setPosts(response.data.posts);
        } catch (error) {
            console.error('Failed to fetch feeds:', error);
            if (error.response?.status === 403) {
                setError(error.response.data.detail);
            } else {
                toast.error('Failed to load feeds');
            }
        } finally {
            if (!silent) setLoading(false);
        }
    };


    const fetchCompany = async () => {
        try {
            const response = await companyAPI.getCurrent();
            setCompany(response.data);
        } catch (error) {
            console.error('Failed to fetch company:', error);
        }
    };

    useEffect(() => {
        fetchPosts();
        fetchCompany();
    }, []);

    const handleCreatePost = () => {
        setEditingPost(null);
        setIsModalOpen(true);
    };

    const handleEditPost = (post) => {
        setEditingPost(post);
        setIsModalOpen(true);
    };

    const handleDeletePost = async (id) => {
        if (!window.confirm('Are you sure you want to delete this post?')) return;

        try {
            await feedAPI.deletePost(id);
            toast.success('Post deleted successfully');
            fetchPosts();
        } catch (error) {
            toast.error('Failed to delete post');
        }
    };

    if (error) {
        return <ModuleDisabled moduleName="feeds" />;
    }

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Company Feeds</h1>
                    <p className="text-muted-foreground">Stay updated with the latest company news and announcements.</p>
                </div>
                {isAdminOrHR() && (
                    <Button onClick={handleCreatePost} className="gap-2">
                        <Plus className="h-4 w-4" />
                        New Post
                    </Button>
                )}
            </div>

            {loading ? (
                <div className="flex h-[400px] items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            ) : posts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[400px] bg-white rounded-xl border border-dashed border-slate-300 gap-4">
                    <div className="h-16 w-16 bg-slate-100 rounded-full flex items-center justify-center">
                        <Newspaper className="h-8 w-8 text-slate-400" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-medium">No posts yet</h3>
                        <p className="text-sm text-slate-500 max-w-xs">
                            {isAdminOrHR()
                                ? "Start sharing updates with your team by creating your first post."
                                : "There are no announcements or posts at the moment."}
                        </p>
                    </div>
                    {isAdminOrHR() && (
                        <Button variant="outline" onClick={handleCreatePost}>
                            Create your first post
                        </Button>
                    )}
                </div>
            ) : (
                <div className="grid gap-6">
                    {posts.map((post) => (
                        <FeedPostCard
                            key={post.id}
                            post={post}
                            company={company}
                            onEdit={isAdminOrHR() ? handleEditPost : null}
                            onDelete={isAdminOrHR() ? handleDeletePost : null}
                            onRefresh={(silent) => fetchPosts(silent)}
                        />

                    ))}
                </div>
            )}

            {isModalOpen && (
                <CreatePostModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSuccess={fetchPosts}
                    post={editingPost}
                />
            )}
        </div>
    );
};

export default Feeds;
